// src/pages/inventory/EditProductPage.jsx - FIXED VERSION
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import ProductForm from "../../components/inventory/ProductForm";
import Button from "../../components/common/Button";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import {
  getProductById,
  updateProduct,
  deleteProduct,
  getProductCategories,
  getProductBrands,
  addProductCategory,
  addProductBrand,
  generateBarcode,
  validateBarcodeUnique
} from "../../services/inventoryService";

const EditProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Load product, categories, and brands
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Get product
        const productResult = await getProductById(id);

        // Process the product data to ensure compatibility with the new form structure
        // FIXED: Don't generate new barcode, preserve the existing one
        const processedProduct = {
          ...productResult,
          // Ensure all automotive fields are present with defaults
          partNumber: productResult.partNumber || '',
          hsnCode: productResult.hsnCode || '',
          modelCompatibility: productResult.modelCompatibility || [],
          supplierName: productResult.supplierName || '',
          purchaseDate: productResult.purchaseDate || '',
          invoiceNo: productResult.invoiceNo || '',
          location: productResult.location || '',
          warrantyMonths: productResult.warrantyMonths || '',
          lastUsedDate: productResult.lastUsedDate || '',
          status: productResult.status || 'Active',
          reorderLevel: productResult.reorderLevel || productResult.lowStockThreshold || '10',
          // Ensure price mapping
          price: productResult.price || productResult.sellingPrice || '',
          // FIXED: Keep existing barcode, only generate if none exists
          barcode: productResult.barcode || generateBarcode(),
          // Ensure brands array (handle both old brand field and new brands array)
          brands: Array.isArray(productResult.brands) ? productResult.brands : (productResult.brand ? [productResult.brand] : []),
          // Ensure dimensions object exists
          dimensions: {
            length: productResult.dimensions?.length || '',
            width: productResult.dimensions?.width || '',
            height: productResult.dimensions?.height || ''
          },
          // Ensure specifications array exists
          specifications: productResult.specifications || [],
          // Ensure other fields
          weight: productResult.weight || '',
          notes: productResult.notes || '',
          isActive: productResult.isActive !== false
        };

        setProduct(processedProduct);

        // Get categories
        const categoriesResult = await getProductCategories();
        setCategories(categoriesResult || []);

        // Get brands
        const brandsResult = await getProductBrands();
        setBrands(brandsResult || []);

        setLoading(false);
      } catch (error) {
        console.error("Error loading product data:", error);
        setError("Failed to load product. Please try again.");
        setLoading(false);
      }
    };

    if (id) {
      loadData();
    }
  }, [id]); // FIXED: Removed dependency on generateBarcode or other functions

  // Handle adding new category
  const handleAddCategory = async (categoryData) => {
    try {
      console.log('Adding new category:', categoryData);
      const newCategory = await addProductCategory(categoryData);

      // Update categories state
      setCategories(prev => [...prev, newCategory]);
      showAlert('Category added successfully!', 'success');

      return newCategory;
    } catch (error) {
      console.error('Error adding category:', error);
      showAlert(error.message || 'Failed to add category. Please try again.', 'error');
      throw error;
    }
  };

  // Handle adding new brand
  const handleAddBrand = async (brandData) => {
    try {
      console.log('Adding new brand:', brandData);
      const newBrand = await addProductBrand(brandData);

      // Update brands state
      setBrands(prev => [...prev, newBrand]);
      showAlert('Brand added successfully!', 'success');

      return newBrand;
    } catch (error) {
      console.error('Error adding brand:', error);
      showAlert(error.message || 'Failed to add brand. Please try again.', 'error');
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (productData) => {
    try {
      setSubmitting(true);
      setError("");

      // FIXED: Only validate barcode uniqueness if it has actually changed
      if (productData.barcode && productData.barcode !== product.barcode) {
        const isBarcodeUnique = await validateBarcodeUnique(productData.barcode, id);
        if (!isBarcodeUnique) {
          showAlert('Barcode already exists. Please use a different barcode.', 'error');
          setSubmitting(false);
          return;
        }
      }

      // Process the product data for automotive spare parts
      const processedData = {
        ...productData,
        vendorId: vendorProfile.uid,
        // Ensure price is mapped correctly
        sellingPrice: productData.price || productData.sellingPrice,
        // Convert string numbers to proper types
        stock: productData.stock ? parseInt(productData.stock, 10) : 0,
        reorderLevel: productData.reorderLevel ? parseInt(productData.reorderLevel, 10) : 10,
        lowStockThreshold: productData.lowStockThreshold || productData.reorderLevel ? parseInt(productData.lowStockThreshold || productData.reorderLevel, 10) : 10,
        price: productData.price ? parseFloat(productData.price) : 0,
        comparePrice: productData.comparePrice ? parseFloat(productData.comparePrice) : null,
        cost: productData.cost ? parseFloat(productData.cost) : null,
        weight: productData.weight ? parseFloat(productData.weight) : null,
        warrantyMonths: productData.warrantyMonths ? parseInt(productData.warrantyMonths, 10) : null,
        // Ensure dimensions are properly formatted
        dimensions: {
          length: productData.dimensions?.length ? parseFloat(productData.dimensions.length) : null,
          width: productData.dimensions?.width ? parseFloat(productData.dimensions.width) : null,
          height: productData.dimensions?.height ? parseFloat(productData.dimensions.height) : null
        },
        // Ensure dates are properly formatted
        purchaseDate: productData.purchaseDate || null,
        lastUsedDate: productData.lastUsedDate || null,
        // FIXED: Use the barcode from form data (which preserves original unless manually changed)
        barcode: productData.barcode || product.barcode,
        // Ensure brands array (instead of single brand)
        brands: Array.isArray(productData.brands) ? productData.brands : (productData.brand ? [productData.brand] : []),
        // Update timestamp
        updatedAt: new Date().toISOString(),
        // Keep original creation timestamp
        createdAt: product.createdAt || new Date().toISOString()
      };

      // Remove old brand field if it exists
      if (processedData.brand) {
        delete processedData.brand;
      }

      console.log('Updating product with barcode:', processedData.barcode);

      // Update product
      await updateProduct(id, processedData);

      showAlert("Automotive spare part updated successfully!", "success");

      // Navigate to inventory page
      navigate("/inventory");
    } catch (error) {
      console.error("Error updating product:", error);
      setError(error.message || "Failed to update product. Please try again.");
      setSubmitting(false);
    }
  };

  // Handle product deletion
  const handleDelete = async () => {
    try {
      await deleteProduct(id, vendorProfile.uid);
      showAlert("Product deleted successfully!", "success");
      navigate("/inventory");
    } catch (error) {
      console.error("Error deleting product:", error);
      setError(error.message || "Failed to delete product. Please try again.");
      setShowDeleteModal(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">
              Edit Automotive Spare Part
            </h1>
          </div>
          <div className="bg-white shadow overflow-hidden sm:rounded-lg p-8">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
            <p className="text-center mt-4 text-gray-500">
              Loading spare part data...
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error && !product) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">
              Edit Automotive Spare Part
            </h1>
          </div>
          <div className="bg-white shadow overflow-hidden sm:rounded-lg p-8">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>Please try again or go back to the inventory page.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate("/inventory")}
              >
                Back to Inventory
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-150"
          aria-label="Go back to previous page"
        >
          <svg
            className="w-5 h-5 mr-2 -ml-1"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="py-6">
          <div className="mb-8 flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Edit Automotive Spare Part
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Update spare part information, barcode, pricing, inventory levels, and supplier details for "{product?.name}".
              </p>
            </div>
            <div>
              <Button
                type="button"
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Delete Product
              </Button>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <ProductForm
            initialData={product}
            categories={categories}
            brands={brands}
            loading={loading}
            submitting={submitting}
            onSubmit={handleSubmit}
            onAddCategory={handleAddCategory}
            onAddBrand={handleAddBrand}
            submitButtonText="Update Spare Part"
            isEdit={true}
             isMainAdmin={vendorProfile?.isMainAdmin === true}  // ADD THIS
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Automotive Spare Part
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete "{product?.name}" (Barcode: {product?.barcode})? This action cannot be undone and will remove all associated data including inventory levels, supplier information, and purchase history.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDelete}
                >
                  Delete Spare Part
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default EditProductPage;